import { createFileRoute } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/site";
import { canonicalLink, hreflangLinks } from "@/lib/seo";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  X,
  Quote,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  Lightbulb,
  ShieldCheck,
  Users,
  Cog,
  Database,
  Clock,
  PiggyBank,
  Truck,
  BarChart3,
  Target,
  Download,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SmartlaneButton } from "@/components/SmartlaneButton";

import hartmannBgAsset from "@/assets/customers/hartmann-smartlane.png.asset.json";
const hartmannBg = hartmannBgAsset.url;

// Logos (CDN asset pointers)
import logoEmonsAsset from "@/assets/logos/emons.png.asset.json";
import logoHartmannAsset from "@/assets/logos/hartmann.png.asset.json";
import logoHeinzAsset from "@/assets/logos/heinz-huber.png.asset.json";
import logoHenselAsset from "@/assets/logos/hensel.png.asset.json";
import logoIgsAsset from "@/assets/logos/igs-schreiner.png.asset.json";
import logoOffergeldAsset from "@/assets/logos/offergeld.png.asset.json";
import logoFiegeAsset from "@/assets/logos/fiege.png.asset.json";
import logoBurattiAsset from "@/assets/logos/buratti-logo.svg.asset.json";

import logoRthAsset from "@/assets/logos/rth.png.asset.json";
import logoWeroAsset from "@/assets/logos/wero.png.asset.json";
import logoWolfAsset from "@/assets/logos/wolf-spedition.png.asset.json";
import logoSchwenkAsset from "@/assets/logos/schwenk-logistik.png.asset.json";
import logoVsAsset from "@/assets/logos/vs-logistics.png.asset.json";
import logoGMAsset from "@/assets/logos/gebrueder-mayer.png.asset.json";

const logoEmons = logoEmonsAsset.url;
const logoHartmann = logoHartmannAsset.url;
const logoHeinz = logoHeinzAsset.url;
const logoHensel = logoHenselAsset.url;
const logoIgs = logoIgsAsset.url;
const logoOffergeld = logoOffergeldAsset.url;
const logoFiege = logoFiegeAsset.url;
const logoBuratti = logoBurattiAsset.url;

const logoRth = logoRthAsset.url;
const logoWero = logoWeroAsset.url;
const logoWolf = logoWolfAsset.url;
const logoSchwenk = logoSchwenkAsset.url;
const logoVs = logoVsAsset.url;
const logoGM = logoGMAsset.url;

// Customer images
import imgEmons from "@/assets/customers/emons.png";
import imgHartmann from "@/assets/customers/hartmann.png";
import imgHeinz from "@/assets/customers/heinzhuber.png";
import imgHensel from "@/assets/customers/hensel.png";
import imgIgs from "@/assets/customers/igs.png";
import imgOffergeld from "@/assets/customers/offergeld.png";
import imgRudolphAsset from "@/assets/customers/rudolph-new.png.asset.json";
const imgRudolph = imgRudolphAsset.url;

// Portraits
import portraitZoltan from "@/assets/people/zoltan-kobor.png";
import portraitRalf from "@/assets/people/ralf-wieland.png";
import portraitUwe from "@/assets/people/uwe-lachmann.png";
import portraitBenediktAsset from "@/assets/people/benedikt-wietel.jpg.asset.json";

const portraitBenedikt = portraitBenediktAsset.url;

/* ───── Data ───── */

const KPIS = [
  { code: "KPI_01", target: 90, suffix: "%", prefix: "−", icon: Clock, label: "Planungszeit reduziert", desc: "Vollautomatisierte Disposition statt manueller Planung." },
  { code: "KPI_02", target: 21, suffix: "%", prefix: "+", icon: Truck, label: "Mehr Ware ausgeliefert", desc: "Höhere Stoppdichte bei gleichem Fuhrpark." },
  { code: "KPI_03", target: 18, suffix: "%", prefix: "−", icon: PiggyBank, label: "Operative Kosten gespart", desc: "Weniger Kilometer. Weniger Zuschläge." },
  { code: "KPI_04", target: 100, suffix: "%", prefix: "", icon: ShieldCheck, label: "Kundenwünsche eingehalten", desc: "Zeitfenster, Restriktionen, Servicequalität." },
];

const HARTMANN_HIGHLIGHTS = [
  "Automatisierte Planung von 100+ Fahrzeugen pro Tag",
  "Integration in bestehende TMS-Landschaft",
  "Nutzung ohne disruptive Eingriffe in bestehende Dispositions- und Hallenprozesse",
  "Signifikant höhere Auslastung des vorhandenen Fuhrparks",
  "Hohe Akzeptanz seitens der Disponenten dank realitätsnaher Planungsergebnisse",
];

interface CustomerCard {
  type: "logo" | "image";
  name: string;
  logo?: string;
  image?: string;
  tagline: string;
  useCase: string;
  isLive?: boolean;
}

const CUSTOMERS: CustomerCard[] = [
  { type: "image", name: "Hartmann International", image: imgHartmann, tagline: "Planung der Ein- und Ausgänge ihrer Stückgutsendungen an zwei Standorten.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "image", name: "IGS Schreiner", image: imgIgs, tagline: "Automatisierte Planung nationaler Landtransporte im Stückgutgeschäft.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "image", name: "Offergeld Logistik", image: imgOffergeld, tagline: "Tägliche Tourenplanung im Bereich FMCG und Haushaltsgeräte an zwei Standorten.", useCase: "FMCG-LOGISTIK", isLive: true },
  { type: "image", name: "Emons", image: imgEmons, tagline: "Tägliche Planung des leichten sowie im schwere Nahverkehrs an über 20 Standorten.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "image", name: "Heinz Huber", image: imgHeinz, tagline: "Täglicher Einsatz zur Entlastung der Disponenten im leichten und schweren Nahverkehr.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "image", name: "Hensel Logistik", image: imgHensel, tagline: "KI-gestützte Disposition für maximale Effizienz in der Stückgutlogistik.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "logo", name: "VS Logistics", logo: logoVs, tagline: "Tägliche Planung der Stück- und Sammelguttransporte.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "logo", name: "Fiege Logistik", logo: logoFiege, tagline: "Tägliche Planung der Touren in der Reifenlogistik.", useCase: "KONTRAKTLOGISTIK", isLive: true },
  { type: "logo", name: "Buratti", logo: logoBuratti, tagline: "Automatisierte Disposition als Großhändler italienischer Feinkostspezialitäten.", useCase: "LEBENSMITTELLOGISTIK", isLive: true },
  { type: "logo", name: "Gebrüder Mayer", logo: logoGM, tagline: "Maximale Effizienz durch KI-gestützte Disposition und Tourenplanung.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
  { type: "image", name: "rudolph t&h", image: imgRudolph, tagline: "Smartlane für die Planung der Luftfrachttransporte.", useCase: "LUFTFRACHTLOGISTIK", isLive: true },
  { type: "logo", name: "Wolf Spedition", logo: logoWolf, tagline: "KI-gestützte Tourenplanung für maximale Effizienz.", useCase: "STÜCKGUTLOGISTIK", isLive: true },
];

const MARQUEE_LOGOS = [
  logoEmons, logoHartmann, logoHeinz, logoHensel, logoIgs, logoOffergeld,
  logoFiege, logoBuratti, logoRth, logoWero, logoWolf,
  logoSchwenk, logoVs, logoGM,
];

const ERFOLGE = [
  { icon: Clock, title: "Bis zu 90 % weniger Dispositionsaufwand", description: "Realitätsnahe Planungsergebnisse reduzieren die operative Belastung und schaffen mehr Freiraum für Kunden und Qualität." },
  { icon: PiggyBank, title: "SONDERZUSCHLÄGE REDUZIEREN", description: "Optimierte Abläufe und eine schnelle Vordisposition verringern Arbeitszeiten in Nachtschichten – inklusive Einsparungen von Zuschlägen." },
  { icon: Truck, title: "Fuhrparkgröße verkleinern", description: "Reduzierung des eigenen Fuhrparks oder der Subunternehmerfahrzeuge bei gleichbleibendem Auftragsvolumen." },
  { icon: BarChart3, title: "BIS ZU 21% MEHR AUFTRÄGE BEI GLEICHEM RESSOURCENEINSATZ", description: "Verbesserte Auslastung und damit höhere Stoppdichte bei gleicher Flotte – für mehr Volumen und mehr Umsatz." },
  { icon: Target, title: "Strafkosten vermeiden", description: "Realistische Planung reduziert Verspätungen und Zeitfensterverletzungen zuverlässig." },
];

const BENEFITS = [
  { icon: Lightbulb, title: "Komplexität reduzieren", description: "Mehr Transparenz im Ablauf durch eine einheitliche Datenbasis – Ihr Team gewinnt an Klarheit und Struktur." },
  { icon: ShieldCheck, title: "Planungssicherheit schaffen", description: "Planung auf konstant hohem Niveau mit durchgehender Verfügbarkeit sorgt für stabile Abläufe, auch bei kurzfristigen Personalausfällen oder Auslastungsspitzen." },
  { icon: Users, title: "Mitarbeiter:innen gezielt entlasten", description: "Durch die Automatisierung langwieriger Planungsprozesse bleibt mehr Zeit für die Kundenbetreuung und die Sicherstellung einer konstant hohen Servicequalität." },
  { icon: Cog, title: "Knowhow nachhaltig sichern", description: "Erfahrungswissen wird digitalisiert, zentral im System gebündelt und für das gesamte Team verfügbar gemacht." },
  { icon: Database, title: "Datenqualität kontinuierlich verbessern", description: "Unser System lernt und sorgt so für immer bessere Planungsgrundlagen, ohne Mehraufwand für Ihr Team." },
];

const TESTIMONIALS = [
  { quote: "Mit Smartlane automatisieren wir nun unsere bislang manuelle Tourenplanung. Die persönliche Betreuung während der Einführung und auch im operativen Betrieb ist super – so macht der Einsatz der Software tatsächlich viel Spaß und spart mir viel Zeit in der Planung.", author: "Zoltán Kóbor", company: "Disponent, Buratti", photo: portraitZoltan },
  { quote: "Mit der standortübergreifenden Implementierung einer Expertenlösung speziell für die Disposition sollen erhebliche Potentiale ausgeschöpft und der Dispositionsprozess transparenter und zukunftsfähiger gestaltet werden.", author: "Ralf Wieland", company: "Geschäftsführer/CEO, Emons", photo: portraitRalf },
  { quote: "Die Disposition im Stückgutbereich ist eine sehr komplexe und zeitaufwendige Angelegenheit. Smartlanes Technologie unterstützt unsere Disponenten dabei, indem sie manuelle Prozesse automatisiert und somit unsere Disponenten enorm entlastet. Diese Entlastung führte dazu, dass wir unsere Servicequalität deutlich steigern konnten und unsere Disponenten ihren Arbeitstag nicht mehr mitten in der Nacht beginnen müssen, sondern ganze fünf Stunden später.", author: "Uwe Lachmann", company: "Geschäftsführer, Hartmann International", photo: portraitUwe },
  { quote: "Die Kombination aus einem starken Produkt, gutem Prozessverständnis und zuverlässigem Service schafft für uns einen echten Mehrwert.", author: "Benedikt Wietel", company: "Prokurist & Bereichsleiter Disposition, rudolph trucking & handling GmbH", photo: portraitBenedikt },
];

/* ───── JSON-LD ───── */

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Start", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Kunden", item: absoluteUrl("/kunden") },
  ],
};

const reviewsJsonLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Smartlane Transport Intelligence",
  description: "KI-basierte Tourenplanungssoftware",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: 5,
    reviewCount: TESTIMONIALS.length,
    bestRating: 5,
    worstRating: 1,
  },
  review: TESTIMONIALS.map((t) => ({
    "@type": "Review",
    reviewBody: t.quote,
    author: { "@type": "Person", name: t.author },
    reviewRating: {
      "@type": "Rating",
      ratingValue: 5,
      bestRating: 5,
      worstRating: 1,
    },
  })),
};

/* ───── Route ───── */

export const Route = createFileRoute("/kunden")({
  head: () => {
    return {
      meta: [
        { title: "Kunden & Referenzen | Smartlane" },
        {
          name: "description",
          content:
            "Über 50 führende Speditionen vertrauen Smartlane: Emons, Hartmann, Hensel, IGS, Offergeld u. v. m. Erfahren Sie, wie Marktführer mit KI-Tourenplanung messbare Erfolge erzielen.",
        },
        { property: "og:title", content: "Kunden & Referenzen | Smartlane" },
        { property: "og:description", content: "Echte Zahlen, echte Erfolge. So planen Marktführer mit Smartlane." },
        { property: "og:url", content: absoluteUrl("/kunden") },
        { property: "og:type", content: "website" },
      ],
      links: [canonicalLink("/kunden"), ...hreflangLinks("/kunden")],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(reviewsJsonLd) },
      ],
    };
  },
  component: KundenPage,
});

/* ═══════════════════════ Page ═══════════════════════ */

function KundenPage() {
  return (
    <>
      <HeroSection />
      <LogoSliderBand />
      <TourSection />
      <FinalCTA />
    </>
  );
}

/* ─────────── LOGO SLIDER ─────────── */
function LogoSliderBand() {
  const loop = [...MARQUEE_LOGOS, ...MARQUEE_LOGOS];
  return (
    <section className="relative bg-white py-8 border-y border-black/5 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10"
        style={{ background: "linear-gradient(to right, #fff, transparent)" }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10"
        style={{ background: "linear-gradient(to left, #fff, transparent)" }}
      />
      <div className="flex gap-20 animate-logo-scroll" style={{ width: "max-content" }}>
        {loop.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className="h-6 md:h-7 w-auto object-contain opacity-60 grayscale brightness-0 hover:opacity-100 hover:grayscale-0 hover:brightness-100 transition-all duration-300"
            loading="lazy"
          />
        ))}
      </div>
    </section>
  );
}

/* ─────────── HERO ─────────── */
function HeroSection() {
  return (
    <section className="relative isolate overflow-hidden bg-rich-black min-h-[100svh] flex items-center">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${hartmannBg})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-rich-black/50 via-rich-black/25 to-rich-black/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-rich-black/60 via-rich-black/20 to-transparent" />
      <div className="absolute inset-0 bg-dot-grid-dark opacity-30 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(55% 50% at 75% 30%, hsl(var(--smartlane-orange) / 0.18), transparent 60%)",
        }}
      />

      <div className="container-wide relative z-10 pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur-md mb-8">
            <span className="h-2 w-2 rounded-full bg-smartlane-orange animate-pulse" />
            <span className="font-body text-xs uppercase tracking-[0.2em] text-white/80">
              SEIT ÜBER 10 JAHREN ERFOLGREICH AM MARKT
            </span>
          </div>

          <h1 className="text-display-xl uppercase tracking-wider text-white mb-8 leading-[0.95]">
            UNSERE KUNDEN BEKOMMEN <span className="text-smartlane-orange">TOURENPLANUNG ALS RUNDUM-SORGLOS-PAKET</span>
          </h1>

          <p className="copy text-white text-lg md:text-xl leading-relaxed mb-10 max-w-2xl">
            Von klein und mittelständischen Speditionen bis zu großen
            Logistikdienstleistern – Smartlane disponiert täglich tausende Nah-
            und Regionalverkehrstouren mit Fokus auf Stückgut- und
            Verteilerlogistik.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <SmartlaneButton to="/kontakt" variant="primary" size="lg">
              LIVE-DEMO ANFRAGEN <ArrowRight className="h-5 w-5" />
            </SmartlaneButton>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/50">
        <span className="font-body text-[10px] uppercase tracking-[0.3em]">Referenzen entdecken</span>
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </div>
    </section>
  );
}

/* ─────────── Tour Section: snaking glowing line ─────────── */

const SECTION_TITLES = [
  { eyebrow: "Messbare Erfolge bei unseren Kunden", title: "Premium-Qualität in Zahlen." },
  { eyebrow: "Case Study · STÜCKGUTLOGISTIK", title: "Hartmann International: REDUZIERUNG DER DISPOSITIONSZEIT UM 5 STUNDEN" },
  { eyebrow: "Bewährt im Echtbetrieb", title: "ÜBER 10 JAHRE VERTRAUEN – EIN AUSZUG UNSERER KUNDEN." },
  { eyebrow: "Das erreichen unsere Kunden", title: "Messbare Erfolge mit Smartlane." },
  { eyebrow: "DAS BEKOMMEN SIE DARÜBER HINAUS", title: "Technologie mit Menschenverstand." },
  { eyebrow: "Kundenstimmen", title: "Was unsere Kunden sagen." },
];

function TourSection() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [progress, setProgress] = useState(0);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    if (pathRef.current) setPathLength(pathRef.current.getTotalLength());
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height;
      const passed = vh / 2 - rect.top;
      const p = Math.max(0, Math.min(1, passed / total));
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const pathD =
    "M 50 0 L 50 50 L 22 50 L 22 150 L 78 150 L 78 250 L 22 250 L 22 350 L 78 350 L 78 450 L 22 450 L 22 550 L 78 550 L 78 600";

  const glowLen = Math.max(pathLength * 0.12, 1);
  const glowOffset = -(progress * pathLength - glowLen / 2);

  return (
    <section ref={wrapperRef} className="relative bg-white overflow-hidden">
      <svg
        className="hidden md:block absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 600"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <filter id="orange-glow-k" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          ref={pathRef}
          d={pathD}
          fill="none"
          stroke="hsl(var(--smartlane-orange))"
          strokeOpacity="0.15"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        {pathLength > 0 && (
          <path
            d={pathD}
            fill="none"
            stroke="hsl(var(--smartlane-orange))"
            strokeOpacity="0.55"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            style={{
              strokeDasharray: `${glowLen} ${pathLength}`,
              strokeDashoffset: glowOffset,
              transition: "stroke-dashoffset 150ms linear",
            }}
          />
        )}
      </svg>

      <div className="relative z-10">

        <SectionTitle {...SECTION_TITLES[1]} align="right" />
        <CaseStudySection />

         <SectionTitle {...SECTION_TITLES[2]} align="left" />
         <LogoGridSection />

        <SectionTitle {...SECTION_TITLES[3]} align="right" />
        <ErfolgeSection />

        <SectionTitle {...SECTION_TITLES[4]} align="left" />
        <TechnologieSection />

        <SectionTitle {...SECTION_TITLES[5]} align="right" />
        <TestimonialsSection />
      </div>
    </section>
  );
}

function SectionTitle({
  eyebrow,
  title,
  align,
}: {
  eyebrow: string;
  title: string;
  align: "left" | "right";
}) {
  return (
    <div className="container-wide pt-28 md:pt-40 pb-10">
      <div className={`max-w-2xl ${align === "right" ? "ml-auto text-right" : ""}`}>
        <div
          className={`inline-flex items-center gap-3 mb-5 ${
            align === "right" ? "flex-row-reverse" : ""
          }`}
        >
          <span className="h-px w-10 bg-smartlane-orange" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-smartlane-orange">
            {eyebrow}
          </span>
        </div>
        <h2 className="text-display-md uppercase tracking-wider text-rich-black leading-tight">
          {title}
        </h2>
      </div>
    </div>
  );
}

/* ─────────── Count-up ─────────── */
function useCountUp(target: number, duration = 1400) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now: number) => {
              const p = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return { ref, value };
}

/* ─────────── KPI Strip (premium cards) ─────────── */
function KpiStrip() {
  return (
    <div className="container-wide pb-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {KPIS.map((k) => (
          <KpiCard key={k.code} {...k} />
        ))}
      </div>
    </div>
  );
}

function KpiCard({
  code,
  icon: Icon,
  prefix,
  target,
  suffix,
  label,
  desc,
}: {
  code: string;
  icon: typeof Clock;
  prefix: string;
  target: number;
  suffix: string;
  label: string;
  desc: string;
}) {
  const { ref, value } = useCountUp(target);
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-rich-black/8 bg-white p-7 lg:p-9 transition-all duration-500 hover:border-smartlane-orange/40 hover:shadow-[0_30px_80px_-30px_hsl(var(--smartlane-orange)/0.35)]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-smartlane-orange/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute inset-0 bg-dot-grid opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none" />

      <div className="relative flex items-center justify-between mb-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-dark-gray/60">
          {code}
        </span>
        <Icon className="w-4 h-4 text-smartlane-orange" strokeWidth={1.6} />
      </div>

      <p className="relative font-heading text-6xl md:text-7xl leading-none text-rich-black mb-3 tabular-nums">
        {prefix}
        <span ref={ref}>{value}</span>
        <span>{suffix}</span>
      </p>

      <p className="relative font-body text-xs uppercase tracking-[0.18em] text-rich-black font-semibold leading-tight mb-3">
        {label}
      </p>
      <p className="relative copy text-dark-gray text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

/* ─────────── Case Study (premium dark) ─────────── */
function CaseStudySection() {
  return (
    <div className="container-wide pb-16">
      <div className="relative rounded-3xl bg-gradient-to-br from-rich-black to-dark-charcoal p-4 md:p-6 lg:p-10 shadow-[0_40px_100px_-30px_hsl(var(--rich-black)/0.6)] overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid-dark opacity-20 pointer-events-none" />
        <div
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--smartlane-orange) / 0.25), transparent 70%)",
          }}
        />

        <div className="relative flex items-center justify-between mb-6 px-2">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-smartlane-orange" />
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            case_study / hartmann_international
          </span>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-smartlane-orange animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
              live
            </span>
          </div>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-xl overflow-hidden ring-1 ring-white/10"
          >
            <iframe
              src="https://www.youtube-nocookie.com/embed/bu9t3yaTlEs?rel=0&modestbranding=1"
              title="Hartmann International – Smartlane Case Study"
              className="w-full h-[280px] md:h-[400px] lg:h-[480px]"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <div className="absolute top-4 left-4 bg-rich-black/80 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 flex items-center gap-2">
              <Play className="w-3.5 h-3.5 text-smartlane-orange" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                Originalstimme Geschäftsführung
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smartlane-orange mb-4 block">
              // ergebnis
            </span>
            <p className="font-heading text-2xl md:text-3xl uppercase tracking-wider text-white mb-6 leading-tight">
              REDUZIERUNG DER DISPOSITIONSZEIT<br />
              <span className="text-smartlane-orange">UM 5 STUNDEN</span>
            </p>
            <ul className="space-y-3 mb-8">
              {HARTMANN_HIGHLIGHTS.map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-smartlane-orange flex-shrink-0 mt-1" />
                  <span className="font-body text-white/80 text-sm leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://cd0ec557.sibforms.com/serve/MUIFAKCdlmDWo7qbm5lPok90IZZxk7WJFQohkA0AyskcJeYIpy2PmTH-jFk8L4kENAIHv_rXazc063iN2ZfiXfDSXYr-S87EbfIC0PtYX8R3kwrXhLORqM8dtBwFcYdNOHbgvc8zJ2JsRy313cE4-_OiQl-GYA_UouGNRmgpE-vzDXi0BCRWUEY2uVZLP3vfN2tOk8pqtJPU0g47"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-smartlane-orange hover:bg-smartlane-orange-glow text-white px-5 py-3.5 rounded-lg transition-all duration-300"
            >
              <Download className="w-4 h-4" />
              <div className="text-left">
                <div className="font-body font-semibold uppercase tracking-wider text-xs">
                  Case Study herunterladen
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/70 mt-0.5">
                  PDF · Kostenlos · Sofort per E-Mail
                </div>
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Logo Grid (interactive) ─────────── */
function LogoGridSection() {
  const [openKey, setOpenKey] = useState<string | null>(null);
  return (
    <div className="container-wide pb-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {CUSTOMERS.map((cell, i) => {
          const key = String(i);
          const isOpen = openKey === key;
          const toggle = () => setOpenKey((p) => (p === key ? null : key));
          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
            >
              <CustomerTile cell={cell} isOpen={isOpen} onToggle={toggle} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function CustomerTile({ cell, isOpen, onToggle }: { cell: CustomerCard; isOpen: boolean; onToggle: () => void }) {
  const isImage = cell.type === "image";
  return (
    <button
      onClick={onToggle}
      className="group relative w-full h-full flex flex-col text-left rounded-2xl overflow-hidden bg-gradient-to-br from-white to-light-gray border border-rich-black/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)] hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)] hover:border-smartlane-orange/40 hover:-translate-y-1 transition-all duration-300"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3 border-b border-rich-black/8">
        <h3 className="font-heading text-base md:text-lg uppercase tracking-wider text-rich-black leading-tight truncate">
          {cell.name}
        </h3>
        {cell.isLive && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-smartlane-orange/10 px-2.5 py-1 shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-smartlane-orange animate-pulse" />
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-smartlane-orange">Live</span>
          </span>
        )}
      </div>

      {/* Visual */}
      <div className="relative bg-white aspect-[4/3] overflow-hidden">
        {isImage ? (
          <img
            src={cell.image}
            alt={cell.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <img
              src={cell.logo}
              alt={cell.name}
              className="max-w-[65%] max-h-[70%] object-contain group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* Footer: industry + one-liner */}
      <div className="flex-1 flex flex-col gap-2 px-5 py-4 border-t border-rich-black/8">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-smartlane-orange">
          {cell.useCase}
        </span>
        <p className="copy text-sm text-dark-gray leading-snug">{cell.tagline}</p>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-30 bg-gradient-to-br from-white to-light-gray backdrop-blur-sm flex flex-col justify-center p-6"
          >
            <span className="font-mono text-[10px] text-smartlane-orange uppercase tracking-[0.2em] mb-2">
              // {cell.useCase}
            </span>
            <h3 className="font-heading text-lg md:text-xl uppercase tracking-wider text-rich-black mb-3">
              {cell.name}
            </h3>
            <p className="font-body text-sm text-dark-gray leading-relaxed">{cell.tagline}</p>
            <div className="absolute top-3 right-3">
              <div className="w-9 h-9 rounded-full bg-rich-black flex items-center justify-center">
                <X className="w-4 h-4 text-white" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}


/* ─────────── Erfolge (Bento / grid) ─────────── */
function ErfolgeSection() {
  return (
    <div className="container-wide pb-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {ERFOLGE.map((it, i) => (
          <div
            key={i}
            className={cn(
              "group relative rounded-2xl bg-white border border-rich-black/8 p-7 lg:p-9 transition-all duration-500 hover:border-smartlane-orange/40 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_hsl(var(--smartlane-orange)/0.3)] overflow-hidden",
              i === 0 && "lg:col-span-2 lg:row-span-1",
            )}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                background:
                  "radial-gradient(70% 60% at 50% 0%, hsl(var(--smartlane-orange) / 0.07), transparent 70%)",
              }}
            />
            <div className="relative flex items-center justify-between mb-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-dark-gray/60">
                /{String(i + 1).padStart(2, "0")} · ERFOLG
              </span>
              <div className="w-10 h-10 rounded-lg bg-rich-black flex items-center justify-center">
                <it.icon className="w-5 h-5 text-smartlane-orange" strokeWidth={1.6} />
              </div>
            </div>
            <h3 className="relative font-heading text-xl md:text-2xl uppercase tracking-wider text-rich-black mb-3 leading-tight">
              {it.title}
            </h3>
            <p className="relative copy text-dark-gray text-sm leading-relaxed">{it.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Technologie (terminal-style dark cards) ─────────── */
function TechnologieSection() {
  return (
    <div className="container-wide pb-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {BENEFITS.map((it, i) => (
          <div
            key={i}
            className="relative bg-rich-black text-white rounded-2xl p-7 lg:p-9 overflow-hidden group hover:-translate-y-1 transition-transform duration-500"
          >
            <div className="absolute inset-0 bg-dot-grid-dark opacity-30" />
            <div
              className="absolute -top-24 -right-24 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, hsl(var(--smartlane-orange) / 0.35), transparent 70%)",
              }}
            />

            <div className="relative flex items-center justify-between mb-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                /{String(i + 1).padStart(2, "0")} · BENEFIT
              </span>
              <it.icon className="w-5 h-5 text-smartlane-orange" strokeWidth={1.6} />
            </div>

            <h3 className="relative font-heading text-xl md:text-2xl uppercase tracking-wider mb-3 leading-tight">
              {it.title}
            </h3>
            <p className="relative copy text-white/65 text-sm leading-relaxed">{it.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Testimonials Carousel (premium dark surface) ─────────── */
function TestimonialsSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const paginate = (d: number) => {
    setDirection(d);
    setIndex((prev) => (prev + d + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  useEffect(() => {
    const t = setInterval(() => paginate(1), 7000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const t = TESTIMONIALS[index];

  const variants = {
    enter: (d: number) => ({ x: d > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d < 0 ? 300 : -300, opacity: 0 }),
  };

  return (
    <div className="container-wide pb-32">
      <div className="relative rounded-3xl bg-gradient-to-br from-rich-black to-dark-charcoal p-6 md:p-12 lg:p-16 shadow-[0_40px_100px_-30px_hsl(var(--rich-black)/0.6)] overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid-dark opacity-20 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 0%, hsl(var(--smartlane-orange) / 0.15), transparent 70%)",
          }}
        />

        <div className="relative max-w-5xl mx-auto">
          <div className="relative">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={index}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                className="flex flex-col md:flex-row items-center gap-8 md:gap-14 px-2"
              >
                <div className="relative shrink-0">
                  <div className="absolute -inset-3 bg-smartlane-orange/20 blur-2xl rounded-full" />
                  <img
                    src={t.photo}
                    alt={t.author}
                    className="relative w-44 h-44 md:w-56 md:h-56 rounded-full object-cover border-2 border-white/10"
                  />
                  <Quote
                    className="absolute -top-2 -right-2 md:top-2 md:-right-4 w-14 h-14 md:w-20 md:h-20 text-smartlane-orange"
                    strokeWidth={0}
                    fill="currentColor"
                    style={{ transform: "scaleX(-1)" }}
                  />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smartlane-orange mb-4 block">
                    // testimonial / {String(index + 1).padStart(2, "0")}
                  </span>
                  <blockquote className="font-body text-lg md:text-xl text-white/85 leading-relaxed mb-6">
                    „{t.quote}"
                  </blockquote>
                  <p className="font-heading text-lg uppercase tracking-wider text-smartlane-orange">
                    {t.author}
                  </p>
                  <p className="font-body text-sm text-white/60 mt-1">{t.company}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-between mt-10">
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setDirection(i > index ? 1 : -1);
                    setIndex(i);
                  }}
                  className={cn(
                    "h-2 rounded-full transition-all duration-300",
                    i === index ? "bg-smartlane-orange w-8" : "bg-white/20 hover:bg-white/40 w-2",
                  )}
                  aria-label={`Zitat ${i + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => paginate(-1)}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-smartlane-orange flex items-center justify-center transition-colors"
                aria-label="Vorheriges Zitat"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => paginate(1)}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-smartlane-orange flex items-center justify-center transition-colors"
                aria-label="Nächstes Zitat"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Final CTA ─────────── */
function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-rich-black py-32 md:py-44">
      <div className="absolute inset-0 bg-dot-grid-dark opacity-30 pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 60% at 50% 50%, hsl(var(--smartlane-orange) / 0.18), transparent 60%)",
        }}
      />
      <div className="container-wide relative z-10 text-center">
        <p className="eyebrow mb-4 text-balance">MÖCHTEN AUCH SIE IHRE DISPOSITION ENTLASTEN UND EFFIZIENTER GESTALTEN?</p>
        <h2 className="text-display-lg uppercase tracking-wider text-white mb-6 text-balance">
          WERDEN AUCH SIE ZUFRIEDENER<br />
          <span className="text-smartlane-orange">SMARTLANE-KUNDE.</span>
        </h2>
        <p className="copy text-white/70 text-lg max-w-2xl mx-auto mb-10">
          Gerne besprechen wir Ihren konkreten Anwendungsfall und zeigen Ihnen in einer persönlichen Demo, welches Potential Smartlane Transport Intelligence für Ihre Disposition bietet.
        </p>
        <div className="flex flex-col gap-4 justify-center items-center">
          <SmartlaneButton to="/kontakt" variant="primary" size="lg">
            LIVE-DEMO ANFRAGEN <ArrowRight className="w-5 h-5" />
          </SmartlaneButton>
          <p className="font-body text-sm text-white/50">
            Unverbindlich · Persönlich · 60 Minuten
          </p>
        </div>
      </div>
    </section>
  );
}
